import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

// public routes only
const PUBLIC_ROUTES = [
  "/",
  "/explore",
  "/blog",
  "/blog/details",
  "/success-stories",
  "/success-stories/details",
  "/privacy-policy",
  "/terms-and-conditions",
];

const normalizePath = (path) => {
  if (!path) return "/";
  return path.replace(/\/+$/, "");
};

const matchesDynamicRoute = (route, path) => {
  if (!route.includes(":")) return false;

  const base = route.split("/:")[0];
  return path.startsWith(base + "/");
};

const fetchSeo = async () => {
  const res = await axios.get(`${API_BASE}/common/seo-data/`);
  return res.data?.data?.seo || [];
};

export default function SEO() {
  const location = useLocation();
  const pathname = normalizePath(location.pathname);

  const { data: seoList = [] } = useQuery({
    queryKey: ["seo"],
    queryFn: fetchSeo,
    staleTime: 1000 * 60 * 10,
  });

  // 🔎 find matching SEO entry
  const seo = seoList.find((item) => {
    // console.log(item, "seoLists");

    const url = normalizePath(item.page_url);

    // ignore absolute URLs
    if (url.startsWith("http")) return false;

    // exact match
    if (url === pathname) return true;

    // dynamic match
    if (matchesDynamicRoute(url, pathname)) return true;

    return false;
  });

  // detect public route
  const isPublic = PUBLIC_ROUTES.some(
    (r) => pathname === r || pathname.startsWith(r + "/")
  );
  // console.log(seo?.page_title, "seo?.page_title");
  // fallback values
  const title = seo?.page_title || "Click4Marry";
  const description =
    seo?.page_description || "Find your life partner on Click4Marry";
  const keywords = seo?.page_keywords?.join(", ") || "matrimony, marriage";

  return (
    <Helmet>
      {/* SEO CONTROL */}
      {!isPublic && <meta name="robots" content="noindex, nofollow" />}

      {/* BASIC */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />

      {/* OG */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
}
